import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldCheck, ArrowLeft, CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";
import { verifySources } from "@/lib/api/ai-research.functions";
import { CitationSetPicker } from "@/components/research/CitationSetPicker";

export const Route = createFileRoute("/education/research/sources")({
  head: () => ({
    meta: [
      { title: "Source Verification — Legal Research Hub | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "Citation-format checker for OSCOLA, Harvard, APA 7 and South African citation styles. Flags pieces you must verify at the primary source — never claims a case actually exists.",
      },
    ],
  }),
  component: SourcesPage,
});

type Item = {
  input: string;
  style_guess?: string;
  verdict?: string;
  notes?: string;
  required_verification?: string[];
};

function VerdictBadge({ v }: { v?: string }) {
  if (v === "FORMAT VALID")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-800">
        <CheckCircle2 className="h-3 w-3" /> Format valid
      </span>
    );
  if (v === "FORMAT QUESTIONABLE")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold text-amber-800">
        <AlertTriangle className="h-3 w-3" /> Questionable
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[10px] font-semibold text-foreground/70">
      <HelpCircle className="h-3 w-3" /> Unrecognised
    </span>
  );
}

function SourcesPage() {
  const run = useServerFn(verifySources);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Item[] | null>(null);
  const [raw, setRaw] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [citationSetId, setCitationSetId] = useState<string | null>(null);

  async function onRun(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    setItems(null);
    setRaw(null);
    try {
      const r = await run({ data: { citations: text, citationSetId: citationSetId ?? undefined } });
      setBalance(r.balance);
      if (r.ok) {
        setItems((r.items as Item[] | null) ?? null);
        setRaw(r.raw ?? null);
      } else {
        setErr(r.error);
      }
    } catch (e: any) {
      const msg = String(e?.message ?? "");
      if (msg.includes("INSUFFICIENT_CREDITS"))
        setErr("You've run out of AI credits. Buy more from your portal credits page.");
      else if (msg.includes("RATE_LIMIT")) setErr("Too many requests right now — try again in a minute.");
      else setErr("AI service is unavailable. Try again shortly.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-narrow py-10">
      <Link to="/education/research" className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-navy">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Research Hub
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <span className="rounded-md bg-navy/5 p-2 text-navy"><ShieldCheck className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-3xl text-navy">Source Verification</h1>
          <p className="text-sm text-muted-foreground">1 credit per batch · format check only</p>
        </div>
      </div>

      <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">What this tool does — and doesn't do</p>
        <p className="mt-1">
          We check whether each line <em>looks like</em> a real citation in OSCOLA, Harvard, APA 7 or SA
          legal-citation form, and we flag the pieces you must confirm at the primary source. We{" "}
          <strong>never</strong> claim a case, statute or article actually exists. Always open the law
          report, the Government Gazette, or the publisher record.
        </p>
      </div>

      <div className="mt-6">
        <CitationSetPicker value={citationSetId} onChange={(id) => setCitationSetId(id)} />
      </div>

      <form onSubmit={onRun} className="mt-4 space-y-4">
        <label className="text-sm font-semibold text-navy">
          Paste citations (one per line)
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          required
          minLength={5}
          maxLength={8000}
          rows={10}
          placeholder={`Carmichele v Minister of Safety and Security 2001 (4) SA 938 (CC)\nCriminal Procedure Act 51 of 1977\nMokoena v S [2024] ZASCA 17`}
          className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Check format (1 credit)
        </button>
      </form>

      {balance !== null && (
        <p className="mt-3 text-xs text-muted-foreground">Credits remaining: <strong>{balance}</strong></p>
      )}
      {err && <p className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-800">{err}</p>}

      {items && (
        <ul className="mt-6 space-y-3">
          {items.map((it, idx) => (
            <li key={idx} className="rounded-lg border border-input bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="font-mono text-sm text-navy">{it.input}</p>
                <VerdictBadge v={it.verdict} />
              </div>
              <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                Style guess: {it.style_guess ?? "unknown"}
              </p>
              {it.notes && <p className="mt-2 text-sm text-foreground/80">{it.notes}</p>}
              {Array.isArray(it.required_verification) && it.required_verification.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-navy">Verify at the primary source:</p>
                  <ul className="mt-1 list-inside list-disc text-sm text-foreground/80">
                    {it.required_verification.map((v, i) => (
                      <li key={i}>{v}</li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {raw && !items && (
        <pre className="mt-6 whitespace-pre-wrap rounded-md border border-input bg-card p-4 text-xs">{raw}</pre>
      )}
    </div>
  );
}
